import type { ReactNode } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/iconNames';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';
import type { ThemeTokens } from '../theme/tokens';

export type BannerTone = 'neutral' | 'dark' | 'info' | 'success' | 'warning' | 'danger';

export type BannerProps = {
  message: string;
  tone?: BannerTone;
  icon?: IconName;
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

function palette(theme: ThemeTokens, tone: BannerTone): { background: string; foreground: string } {
  const colors = theme.colors;
  switch (tone) {
    case 'dark':
      return { background: colors.dark, foreground: colors.textInverse };
    case 'info':
      return { background: colors.infoSubtle, foreground: colors.onInfoSubtle };
    case 'success':
      return { background: colors.successSubtle, foreground: colors.onSuccessSubtle };
    case 'warning':
      return { background: colors.warningSubtle, foreground: colors.onWarningSubtle };
    case 'danger':
      return { background: colors.dangerSubtle, foreground: colors.onDangerSubtle };
    case 'neutral':
    default:
      return { background: colors.surfaceAlt, foreground: colors.text };
  }
}

export function Banner({
  message,
  tone = 'neutral',
  icon,
  actionLabel,
  onAction,
  children,
  style,
  testID,
}: BannerProps) {
  const theme = useTheme();
  const colors = palette(theme, tone);

  return (
    <View
      accessibilityRole="alert"
      testID={testID}
      style={[
        styles.banner,
        {
          backgroundColor: colors.background,
          paddingVertical: theme.spacing(3),
          paddingHorizontal: theme.spacing(4),
          gap: theme.spacing(3),
        },
        style,
      ]}
    >
      {icon !== undefined ? <Icon name={icon} size="sm" color={colors.foreground} /> : null}

      <View style={styles.body}>
        <Text weight="semibold" color={colors.foreground}>
          {message}
        </Text>
        {children}
      </View>

      {actionLabel !== undefined ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => void onAction?.()}
          style={({ pressed }) => [
            {
              borderColor: colors.foreground,
              borderRadius: theme.radius.sm,
              borderWidth: StyleSheet.hairlineWidth,
              paddingVertical: theme.spacing(1),
              paddingHorizontal: theme.spacing(3),
            },
            pressed && styles.pressed,
          ]}
        >
          <Text variant="label" color={colors.foreground}>
            {actionLabel}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  banner: { flexDirection: 'row', alignItems: 'center' },
  body: { flex: 1 },
  pressed: { opacity: 0.7 },
});
